/// <reference types="gtag.js" />

/**
 * Shared typing + accessors for `window.dataLayer` and the `gtag` shim that
 * writes to it.
 *
 * Three writers share the same global array: the consent bootstrap and the
 * GA adapter push `Arguments` objects through `gtag` (Google's canonical
 * snippet shape), and the GTM dataLayer adapter pushes plain event objects.
 * The global declaration lives here — owned by none of them — so removing
 * any one (the GA adapter's retirement is an open PX-262 decision) doesn't
 * strand the others' typing.
 */
export type DataLayerEntry = IArguments | Record<string, unknown>;

declare global {
  // `var` is the canonical way to extend globalThis under `declare global`.
  var dataLayer: DataLayerEntry[] | undefined;
}

/**
 * Returns the page's dataLayer, creating the array if absent. Creating it
 * before (or without) any Google script is the standard, documented
 * pattern — entries queue in a plain array and gtag.js / gtm.js replay
 * them if and when they load.
 */
export function getOrCreateDataLayer(): DataLayerEntry[] {
  const dataLayer = globalThis.dataLayer ?? [];
  globalThis.dataLayer = dataLayer;
  return dataLayer;
}

/**
 * Returns the page's `gtag`, defining it if absent. Whichever caller gets
 * here first defines it and the rest share that one function, so a page can
 * never end up with two shims queueing onto the same array.
 */
export function getOrCreateGtag(): Gtag.Gtag {
  const existing = globalThis.gtag;
  if (existing) return existing;

  const dataLayer = getOrCreateDataLayer();
  const gtag: Gtag.Gtag = function () {
    // Match Google's canonical snippet exactly: gtag.js and gtm.js consume
    // the Arguments object shape from dataLayer, not an arbitrary event
    // object.
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  };
  globalThis.gtag = gtag;
  return gtag;
}
